import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { QUOTES_KEY, type Version } from './types';
import { DialogLayer } from '../ui/Dialog';

export type PacklistDiffRow = { itemId: string; name: string; inQuote: number; inPacklist: number };

/**
 * "Porównaj ze sprzętówką": the equipment whose quantity differs between the shown version and the event's packlist
 * (no amounts). Works for any version: after the packlist changed under a sent quote it shows what a new version
 * needs. `mayFill`: the way on ("Uzupełnij ze sprzętówki": the newest version, never a sent one).
 */
export function PacklistCompareDialog({ version, mayFill, mayGenerate = false, busy, onFill, onGenerate, onClose }: { version: Version; mayFill: boolean; mayGenerate?: boolean; busy: boolean; onFill: () => void; onGenerate?: () => void; onClose: () => void }) {
  const diff = useQuery({ queryKey: [...QUOTES_KEY, 'packlist-diff', version.id], queryFn: () => api<{ diff: PacklistDiffRow[] }>(`/api/quotes/versions/${version.id}/packlist-diff`) });
  const rows = diff.data?.diff ?? [];
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel q-compare" role="dialog" aria-modal="true" aria-labelledby="pl-compare-title" data-feature-id="quotes.packlist_compare">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="pl-compare-title">Porównaj ze sprzętówką</h2>
        <p className="muted">Wersja v{version.number} kosztorysu i sprzętówka wydarzenia: tylko sprzęt, którego ilość się różni.</p>
        {diff.isLoading && (
          <p className="muted" role="status">
            Wczytywanie…
          </p>
        )}
        {diff.error && (
          <p className="alert" role="alert">
            {diff.error instanceof ApiError ? diff.error.message : 'Nie udało się porównać'}
          </p>
        )}
        {diff.data && rows.length === 0 && <p>Sprzęt w kosztorysie i w sprzętówce się zgadza.</p>}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="table" aria-label="Różnice kosztorysu i sprzętówki">
              <thead>
                <tr>
                  <th scope="col">Pozycja</th>
                  <th scope="col" className="num">
                    Kosztorys
                  </th>
                  <th scope="col" className="num">
                    Sprzętówka
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.itemId}>
                    <td>
                      {r.name}
                      <span className="q-kind">{r.inQuote === 0 ? 'tylko w sprzętówce' : r.inPacklist === 0 ? 'tylko w kosztorysie' : 'inna ilość'}</span>
                    </td>
                    <td className="num">{r.inQuote}</td>
                    <td className="num">{r.inPacklist}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="actions">
          {mayFill && rows.length > 0 && (
            <button className="btn primary" type="button" disabled={busy} onClick={onFill}>
              Kosztorys ze sprzętówki
            </button>
          )}
          {/* owner 2026-10-23 20:35: both ways here too */}
          {mayGenerate && onGenerate && rows.length > 0 && (
            <button className="btn" type="button" disabled={busy} onClick={onGenerate}>
              Sprzętówka z kosztorysu
            </button>
          )}
          <button className="btn" type="button" onClick={onClose} autoFocus>
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}
